import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import Footer from '../../components/Footer';
import AppStoreBadges from '../../components/AppStoreBadges';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import MarketingScreenshot from '../../components/MarketingScreenshot';
import ContextualMedia from '../../components/ContextualMedia';
import ScreenshotDetail from '../../components/ScreenshotDetail';
import FeatureIcon, { ICON_TONES } from '../../components/FeatureIcon';
import HostingRosterEvidence from './HostingRosterEvidence';
import { TurnoutGroup, CrewAndSponsors } from './HostingGroups';
import { selectedCaptureSrc } from '../../lib/selectedCaptures';

const photoHeading = '#f8fafc';
const photoBody = 'rgba(226,232,240,0.9)';

const AnimatedSection: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1, rootMargin: '0px' });
  const reducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return (
    <div
      ref={ref}
      style={
        reducedMotion
          ? undefined
          : {
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: `opacity 1.2s ease-out ${delay}ms, transform 1s ease-out ${delay}ms`
            }
      }
    >
      {children}
    </div>
  );
};

const Hosting: React.FC = () => {
  const { theme, dsColors, colors } = useTheme();

  const heroPrimary = colors.textPrimary;
  const heroSecondary = colors.textSecondary;
  const cardBorder = theme === 'light' ? 'rgba(0,0,0,0.1)' : colors.border;
  const bandBg = theme === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.02)';
  const eyebrow = 'text-xs sm:text-sm font-bold uppercase tracking-[0.2em] mb-3';
  const sectionTitle = 'text-3xl sm:text-4xl font-bold mb-4';
  const sectionLead = 'text-lg sm:text-xl max-w-2xl leading-relaxed';

  return (
    <div className="min-h-screen hosting-page" style={{ backgroundColor: colors.background }}>
      {/* Preload this route's hero so it wins against deferred below-fold media. */}
      <link
        rel="preload"
        as="image"
        href="/assets/images/marketing-concepts/ridepulse-sportbikes-coastal-ride-text-right-v1.webp"
        {...{ fetchpriority: 'high' }}
      />
      {/* 1 · Hero — coastal ride; copy in the designed right-side open water/sky */}
      <section className="relative bg-[#0a0d14] md:bg-transparent">
        <div className="md:hidden aspect-[16/10] bg-cover bg-[position:30%_center]" style={{ backgroundImage: `url('/assets/images/marketing-concepts/ridepulse-sportbikes-coastal-ride-text-right-v1.webp')` }} role="img" aria-label="Sportbikes riding a coastal road along the ocean" />
        <div
          className="absolute inset-0 hidden md:block bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(260deg, rgba(8,10,16,0.7) 0%, rgba(8,10,16,0.5) 45%, rgba(8,10,16,0.14) 72%, transparent 88%), url('/assets/images/marketing-concepts/ridepulse-sportbikes-coastal-ride-text-right-v1.webp')`
          }}
        />
        <div className="relative container mx-auto px-4 sm:px-6 max-w-6xl py-16 md:py-32 lg:py-40 md:flex md:justify-end">
          <div className="max-w-2xl text-center md:text-right mx-auto md:mx-0">
            <p className={eyebrow} style={{ color: dsColors.primary }}>Event Management</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-[1.05]" style={{ color: photoHeading, textWrap: 'balance' }}>
              Host your next event.
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl mb-8 leading-relaxed md:ml-auto" style={{ color: photoBody, textWrap: 'pretty' }}>
              Choose the plan, shape the turnout and bring riders together. Keep the details with the event, from the first invitation to the final feedback.
            </p>
            <div className="flex justify-center md:justify-end mb-8">
              <Link
                to="/help#create-event"
                className="inline-flex items-center px-6 py-3 rounded-full font-bold hover:underline focus:outline-none focus-visible:ring-2"
                style={{ backgroundColor: dsColors.primary, color: '#ffffff', '--tw-ring-color': dsColors.primary } as React.CSSProperties}
              >
                See how to create an event
              </Link>
            </div>
            <div className="flex justify-center md:justify-end gap-4 flex-wrap">
              <AppStoreBadges size="large" showApple showAndroid />
            </div>
          </div>
        </div>
      </section>

      {/* 2 · Create — the event form beside its copy, turnout options below */}
      <section className="py-16 md:py-24" style={{ backgroundColor: bandBg }}>
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          <AnimatedSection>
            <ContextualMedia
              media={
                <MarketingScreenshot
                  base="snapshots/20261004T181317Z/Events/iOS - Events - Create Event"
                  alt="The New Event form in RidePulse with event type, motorcycle selection, title, description and visibility choices before publication"
                  caption="Creating an event."
                />
              }
            >
              <div className="max-w-xl">
                <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>Create the event you want to go to.</h2>
                <p className={sectionLead} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                  Coffee on Saturday, a Honda meetup or a weekend tour. Set the meetup, time and plan, then give riders a place to join.
                </p>
              </div>
              <TurnoutGroup />
              <div className="mt-6 max-w-xl">
                <h3 className="text-base font-semibold" style={{ color: heroPrimary, textWrap: 'balance' }}>Keep riding with your crew.</h3>
                <p className="text-sm leading-relaxed mt-2" style={{ color: heroSecondary, textWrap: 'pretty' }}>
                  Not every event needs to be open to everyone. Create an invite-only event and share the invitation with the riders you want to bring together. Keep the plan, bikes and conversation organized in RidePulse.
                </p>
              </div>
              <p className="text-sm mt-8 max-w-xl" style={{ color: heroSecondary, textWrap: 'pretty' }}>
                Weather information for the meetup place and time can help with planning when a forecast is available.
              </p>
              <p className="mt-6">
                <Link
                  to="/help#create-event"
                  className="inline-flex items-center font-semibold hover:underline focus:outline-none focus-visible:ring-2 rounded"
                  style={{ color: dsColors.primary, '--tw-ring-color': dsColors.primary, minHeight: '44px' } as React.CSSProperties}
                >
                  Event setup in Help →
                </Link>
              </p>
            </ContextualMedia>
          </AnimatedSection>
        </div>
      </section>

      {/* 3 · Know who's coming — copy, landscape roster, crew and sponsors */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          <AnimatedSection>
            <div className="max-w-2xl mb-10">
              <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>Know who’s coming.</h2>
              <p className={sectionLead} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                See the riders joining and the motorcycles they’re bringing. Keep questions and changes in the event chat.
              </p>
            </div>
            <HostingRosterEvidence />
            <CrewAndSponsors />
            <p className="text-sm mt-8 max-w-2xl" style={{ color: heroSecondary, textWrap: 'pretty' }}>
              Share the event so riders can find the plan.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* 4 · Arrival — centered pre-start console showcase with labels */}
      <section className="py-16 md:py-24" style={{ backgroundColor: bandBg }}>
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          <AnimatedSection>
            <div className="max-w-2xl mx-auto text-center mb-10">
              <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>When everyone starts arriving.</h2>
              <p className={`${sectionLead} mx-auto`} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                Keep arrivals, crew and the event conversation in view as the group comes together.
              </p>
            </div>
            <div className="host-console">
              <p className="host-console-label host-console-checkins" style={{ color: heroSecondary }}>
                <span className="inline-flex" style={{ color: dsColors[ICON_TONES.safety] }} aria-hidden="true">
                  <FeatureIcon name="checkin" size={20} />
                </span>
                Check-ins
              </p>
              <div className="host-console-media">
                <ScreenshotDetail
                  src={selectedCaptureSrc('hostTools', theme)}
                  crop={{ x: 0, y: 0, width: 1320, height: 2868 }}
                  alt="RidePulse host controls for an event: roles, sponsors, roster, transfer host, cancel and event appearance"
                  caption="Host controls."
                  fullImageLabel="View the full event management screen"
                />
              </div>
              <p className="host-console-label host-console-crew" style={{ color: heroSecondary }}>
                <span className="inline-flex" style={{ color: dsColors[ICON_TONES.community] }} aria-hidden="true">
                  <FeatureIcon name="users" size={20} />
                </span>
                Crew
              </p>
              <p className="host-console-label host-console-chat" style={{ color: heroSecondary }}>
                <span className="inline-flex" style={{ color: dsColors[ICON_TONES.community] }} aria-hidden="true">
                  <FeatureIcon name="chat" size={20} />
                </span>
                Event chat
              </p>
            </div>
            <p className="text-center mt-8">
              <Link
                to="/help#check-in"
                className="inline-flex items-center text-sm font-semibold hover:underline focus:outline-none focus-visible:ring-2 rounded"
                style={{ color: dsColors.primary, '--tw-ring-color': dsColors.primary, minHeight: '44px' } as React.CSSProperties}
              >
                Check-in in Help →
              </Link>
            </p>
            <div className="mt-10 pt-8 max-w-2xl mx-auto" style={{ borderTop: `1px solid ${cardBorder}` }}>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-center text-center sm:text-left">
                <span className="inline-flex flex-shrink-0 mx-auto sm:mx-0" style={{ color: dsColors[ICON_TONES.community] }} aria-hidden="true">
                  <FeatureIcon name="map" size={22} />
                </span>
                <p className="text-sm leading-relaxed" style={{ color: heroSecondary, textWrap: 'pretty' }}>
                  With GroupPulse, a Pro host can enable location sharing for the ride. Each rider chooses how to share.{' '}
                  <Link
                    to="/grouppulse"
                    className="font-semibold hover:underline focus:outline-none focus-visible:ring-2 rounded"
                    style={{ color: dsColors.primary, '--tw-ring-color': dsColors.primary } as React.CSSProperties}
                  >
                    How GroupPulse works →
                  </Link>
                </p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 5 · Close — quiet pointer to history, feedback and Pro */}
      <section className="py-10 md:py-14">
        <div className="container mx-auto px-4 sm:px-6 max-w-2xl">
          <AnimatedSection>
            <h2 className="text-2xl sm:text-3xl font-bold mb-4" style={{ color: heroPrimary, textWrap: 'balance' }}>
              Build on the events you host.
            </h2>
            <p className="text-base sm:text-lg leading-relaxed" style={{ color: heroSecondary, textWrap: 'pretty' }}>
              Keep your hosting history and feedback together. Pro adds a closer look at participation and event performance.
            </p>
            <p className="flex flex-wrap gap-x-6 gap-y-2 mt-5">
              <Link
                to="/social#reputation"
                className="font-semibold hover:underline focus:outline-none focus-visible:ring-2 rounded"
                style={{ color: dsColors.primary, '--tw-ring-color': dsColors.primary } as React.CSSProperties}
              >
                Feedback and reputation →
              </Link>
              <Link
                to="/pro"
                className="font-semibold hover:underline focus:outline-none focus-visible:ring-2 rounded"
                style={{ color: dsColors.primary, '--tw-ring-color': dsColors.primary } as React.CSSProperties}
              >
                Explore Pro →
              </Link>
            </p>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Hosting;
